import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Ban, CheckCircle, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import MemberFormDialog from './MemberFormDialog';
import { API, FAMILIES_MEMBERS_PER_PAGE, paginate, getTotalPages, toggleSelectItem, toggleSelectAll, errDetail } from './adminShared';

export default function MembersTab({ families, reload, authCfg }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [memberDialog, setMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const searchMembers = async (familyId) => {
    if (!familyId || familyId.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/admin/members`, { ...authCfg, params: { family_id: familyId.trim() } });
      setResults(response.data || []);
    } catch (error) {
      setResults([]);
    }
    setSearching(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search) {
        searchMembers(search);
      } else {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [search]);

  const refresh = () => {
    reload();
    if (search) searchMembers(search);
  };

  const paginated = paginate(results, page, FAMILIES_MEMBERS_PER_PAGE);

  const handleDelete = (serialNumber) => {
    setConfirm({
      title: `Delete member "${serialNumber}"`,
      description: 'Their past claims will be kept for historical records, but the member will no longer appear in searches or be eligible for new claims.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/members/${serialNumber}`, authCfg);
          toast.success('Member deleted successfully');
          refresh();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to delete member'));
        }
      },
    });
  };

  const handleSuspendToggle = (member) => {
    const suspend = member.status !== 'Suspended';
    setConfirm({
      title: `${suspend ? 'Suspend' : 'Unsuspend'} member "${member.serial_number}"`,
      description: suspend ? 'The member will be blocked from new claims until reactivated. The reason is shown to hospitals when the member is searched.' : 'The member will be eligible for claims again.',
      confirmLabel: suspend ? 'Suspend' : 'Unsuspend',
      destructive: suspend,
      withReason: suspend,
      reasonRequired: true,
      reasonLabel: 'Reason for suspension (required)',
      reasonPlaceholder: 'e.g. Unpaid premium, fraudulent claim, member left scheme…',
      onConfirm: async (reason) => {
        if (suspend && !(reason || '').trim()) { toast.error('A reason for the suspension is required'); return; }
        try {
          await axios.post(`${API}/admin/members/${member.serial_number}/${suspend ? 'suspend' : 'unsuspend'}`, { reason: (reason || '').trim() }, authCfg);
          toast.success(`Member ${suspend ? 'suspended' : 'unsuspended'} successfully`);
          refresh();
        } catch (error) {
          toast.error(errDetail(error, `Failed to ${suspend ? 'suspend' : 'unsuspend'} member`));
        }
      },
    });
  };

  const handleBulkDelete = () => {
    setConfirm({
      title: `Delete ${selected.length} selected member(s)`,
      description: 'Their past claims will be retained for historical records.',
      confirmLabel: `Delete ${selected.length}`,
      destructive: true,
      onConfirm: async () => {
        try {
          const res = await axios.post(`${API}/admin/members/bulk-delete`, { serial_numbers: selected }, authCfg);
          toast.success(res.data?.message || `Deleted ${selected.length} member(s)`);
          setSelected([]);
          refresh();
        } catch (error) {
          toast.error(errDetail(error, 'Bulk delete failed'));
        }
      },
    });
  };

  const handleBulkSuspend = (suspend) => {
    setConfirm({
      title: `${suspend ? 'Suspend' : 'Activate'} ${selected.length} selected member(s)`,
      description: `The selected members will be ${suspend ? 'suspended' : 'activated'}.`,
      confirmLabel: suspend ? 'Suspend' : 'Activate',
      destructive: suspend,
      withReason: suspend,
      reasonRequired: true,
      reasonLabel: 'Reason for suspension (required)',
      reasonPlaceholder: 'e.g. Unpaid premium, fraudulent claim, member left scheme…',
      onConfirm: async (reason) => {
        if (suspend && !(reason || '').trim()) { toast.error('A reason for the suspension is required'); return; }
        let ok = 0, fail = 0;
        for (const memberId of selected) {
          try {
            await axios.put(`${API}/admin/members/${memberId}`, suspend ? { status: 'Suspended', suspension_reason: reason.trim() } : { status: 'Active' }, authCfg);
            ok++;
          } catch {
            fail++;
          }
        }
        toast.success(`${suspend ? 'Suspended' : 'Activated'} ${ok} member(s)${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelected([]);
        refresh();
      },
    });
  };

  return (
    <Card>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
      <MemberFormDialog open={memberDialog} onOpenChange={setMemberDialog} member={editingMember} families={families} authCfg={authCfg} onSaved={refresh} />
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Members</CardTitle>
        <div className="flex gap-2">
          {selected.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleBulkSuspend(false)} className="border-green-500 text-green-700 hover:bg-green-50">
                <CheckCircle className="w-4 h-4 mr-2" />
                Activate {selected.length} Selected
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleBulkSuspend(true)} className="border-orange-500 text-orange-700 hover:bg-orange-50">
                <Ban className="w-4 h-4 mr-2" />
                Suspend {selected.length} Selected
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete {selected.length} Selected
              </Button>
            </>
          )}
          <Button onClick={() => { setEditingMember(null); setMemberDialog(true); }} data-testid="add-member-btn">
            <Plus className="w-4 h-4 mr-2" />Add Member
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Enter Family ID to view members (e.g., SEC-2413)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="member-search-input" />
          </div>
          {search && (
            <p className="text-sm text-gray-500 mt-2">
              {results.length > 0
                ? `Found ${results.length} member(s) in family ${search.toUpperCase()}`
                : `No members found for family ID "${search}"`}
            </p>
          )}
          {searching && <p className="text-sm text-blue-600 mt-2">Searching...</p>}
          {!search && <p className="text-sm text-amber-600 mt-2">Enter a Family ID to view its members</p>}
        </div>

        {search && !searching && results.length > 0 && (
          <div className="mb-3 text-sm text-gray-600">
            Showing {((page - 1) * FAMILIES_MEMBERS_PER_PAGE) + 1} - {Math.min(page * FAMILIES_MEMBERS_PER_PAGE, results.length)} of {results.length} members
            {selected.length > 0 && <span className="ml-2 text-blue-600 font-semibold">({selected.length} selected)</span>}
          </div>
        )}

        {search && !searching ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-center p-3 text-sm font-semibold w-12">
                    <Checkbox
                      checked={selected.length === paginated.length && paginated.length > 0}
                      onCheckedChange={() => toggleSelectAll(paginated, selected, setSelected, 'serial_number')}
                    />
                  </th>
                  <th className="text-left p-3 text-sm font-semibold">Serial Number</th>
                  <th className="text-left p-3 text-sm font-semibold">Name</th>
                  <th className="text-left p-3 text-sm font-semibold">Family ID</th>
                  <th className="text-left p-3 text-sm font-semibold">Relationship</th>
                  <th className="text-center p-3 text-sm font-semibold">Status</th>
                  <th className="text-center p-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length > 0 ? paginated.map((member) => (
                  <tr key={member.serial_number} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-center">
                      <Checkbox
                        checked={selected.includes(member.serial_number)}
                        onCheckedChange={() => toggleSelectItem(member.serial_number, selected, setSelected)}
                      />
                    </td>
                    <td className="p-3 text-sm font-medium">{member.serial_number}</td>
                    <td className="p-3 text-sm">{member.first_name} {member.middle_name} {member.last_name}</td>
                    <td className="p-3 text-sm">{member.family_id}</td>
                    <td className="p-3 text-sm">{member.relationship === 'Principle' ? 'Principal' : member.relationship}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        member.status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {member.status || 'Active'}
                      </span>
                      {member.status === 'Suspended' && member.suspension_reason && (
                        <div className="mt-1 text-xs text-red-700 max-w-[200px] mx-auto" data-testid={`member-suspension-reason-${member.serial_number}`}>{member.suspension_reason}</div>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingMember(member); setMemberDialog(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {member.status === 'Suspended' ? (
                          <Button variant="ghost" size="sm" onClick={() => handleSuspendToggle(member)} className="text-green-600" title="Unsuspend Member">
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleSuspendToggle(member)} className="text-orange-600" title="Suspend Member">
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(member.serial_number)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">
                      No members found for family ID "{search}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border rounded-lg p-12 text-center bg-gray-50">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">Enter a Family ID above to view its members</p>
            <p className="text-gray-400 text-sm mt-2">Example: SEC-2413, GS-10717</p>
          </div>
        )}

        {search && results.length > FAMILIES_MEMBERS_PER_PAGE && (
          <div className="mt-6">
            <Pagination currentPage={page} totalPages={getTotalPages(results.length, FAMILIES_MEMBERS_PER_PAGE)} onPageChange={setPage} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
